import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { downloadGgFile } from "@/services/genteGestao";
import {
  ESCUTA_TIPO_OPCOES,
  enviarManifestacao,
  extractEscutaError,
  pdfProtocoloUrl,
  type EscutaPublica,
} from "@/services/escuta";

const VAZIO = {
  tipo: "",
  assunto: "",
  relato: "",
  local: "",
  quando: "",
  envolve: "",
  identificar: false,
  nome: "",
  setor: "",
  contato: "",
  querRetorno: false,
};

export function EscutaPublicaPage() {
  const [form, setForm] = useState(VAZIO);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [registrada, setRegistrada] = useState<EscutaPublica | null>(null);
  const [baixando, setBaixando] = useState(false);
  const [protocoloConsulta, setProtocoloConsulta] = useState("");

  function set<K extends keyof typeof VAZIO>(key: K, value: (typeof VAZIO)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const item = await enviarManifestacao(form);
      setRegistrada(item);
    } catch (err) {
      setErro(extractEscutaError(err, "Não foi possível registrar a manifestação."));
    } finally {
      setEnviando(false);
    }
  }

  async function baixarPdf() {
    if (!registrada) return;
    setBaixando(true);
    try {
      await downloadGgFile(pdfProtocoloUrl(registrada.protocolo), `escuta-${registrada.protocolo}.pdf`, "application/pdf");
    } catch (err) {
      setErro(extractEscutaError(err, "Não foi possível baixar o PDF."));
    } finally {
      setBaixando(false);
    }
  }

  return (
    <div className="flex h-dvh min-h-0 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain bg-slate-100 text-slate-800">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-xl items-center justify-center px-4 py-3">
          <span className="text-sm font-bold tracking-wide text-brand-700">SEMOG · Canal de Escuta Ativa</span>
        </div>
      </header>
      <main className="mx-auto w-full max-w-xl px-4 py-6 pb-28">
        {registrada ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
            <h1 className="text-xl font-bold">Manifestação registrada</h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Guarde este protocolo. Se você optou pelo anonimato, ele é a única forma de consultar o andamento.
            </p>
            <p className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-center text-lg font-bold tracking-wide text-brand-700">
              {registrada.protocolo}
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <Button loading={baixando} onClick={() => void baixarPdf()}>
                Baixar PDF com QR Code
              </Button>
              <Link
                to={`/escuta/p/${encodeURIComponent(registrada.protocolo)}`}
                className="inline-flex h-10 items-center justify-center rounded-lg bg-slate-100 px-4 text-sm font-medium text-slate-700 hover:bg-slate-200"
              >
                Acompanhar protocolo
              </Link>
              <Button
                variant="ghost"
                onClick={() => {
                  setRegistrada(null);
                  setForm(VAZIO);
                }}
              >
                Nova manifestação
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
            <div>
              <h1 className="text-xl font-bold">Canal de Escuta Ativa SEMOG</h1>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Espaço seguro e confidencial para registrar opinião, sugestão, reclamação, dúvida, elogio ou denúncia.
                Também cabe aqui situação de assédio, conflito, sobrecarga de trabalho e risco à saúde ou à segurança.
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Sua manifestação será analisada com responsabilidade e sigilo. Não há retaliação por usar este canal.
                Identificar-se é uma escolha sua.
              </p>
            </div>
            {erro && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erro}</p>}
            <Select
              label="Tipo"
              value={form.tipo}
              options={ESCUTA_TIPO_OPCOES.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(e) => set("tipo", e.target.value)}
              required
            />
            <Input label="Assunto" value={form.assunto} maxLength={120} onChange={(e) => set("assunto", e.target.value)} required />
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Relato</span>
              <textarea
                className="min-h-36 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600"
                value={form.relato}
                maxLength={4000}
                required
                onChange={(e) => set("relato", e.target.value)}
              />
            </label>
            <Input label="Onde aconteceu" value={form.local} maxLength={120} onChange={(e) => set("local", e.target.value)} hint="Opcional" />
            <Input label="Quando" type="date" value={form.quando} onChange={(e) => set("quando", e.target.value)} hint="Opcional" />
            <Input
              label="Envolve alguém"
              value={form.envolve}
              maxLength={160}
              onChange={(e) => set("envolve", e.target.value)}
              hint="Opcional. Nome ou cargo, se quiser informar."
            />
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.identificar}
                onChange={(e) => set("identificar", e.target.checked)}
              />
              <span>Quero me identificar. Se desmarcado, o registro não guarda nome, setor nem contato.</span>
            </label>
            {form.identificar && (
              <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <Input label="Nome" value={form.nome} maxLength={120} onChange={(e) => set("nome", e.target.value)} required />
                <Input label="Setor" value={form.setor} maxLength={80} onChange={(e) => set("setor", e.target.value)} />
                <Input
                  label="Contato"
                  value={form.contato}
                  maxLength={120}
                  onChange={(e) => set("contato", e.target.value)}
                  hint="E-mail ou telefone, se quiser retorno direto."
                />
              </div>
            )}
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.querRetorno}
                onChange={(e) => set("querRetorno", e.target.checked)}
              />
              <span>Quero um retorno. O andamento também fica disponível pelo protocolo.</span>
            </label>
            <Button type="submit" loading={enviando}>
              Enviar manifestação
            </Button>
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Já tenho um protocolo</p>
              <div className="mt-2 flex gap-2">
                <input
                  className="h-10 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm uppercase"
                  value={protocoloConsulta}
                  placeholder="EA-XXXXXXXX"
                  onChange={(e) => setProtocoloConsulta(e.target.value.toUpperCase())}
                />
                <Link
                  to={protocoloConsulta.trim() ? `/escuta/p/${encodeURIComponent(protocoloConsulta.trim())}` : "/escuta"}
                  className="inline-flex h-10 items-center rounded-lg bg-slate-100 px-3 text-sm font-medium text-slate-700"
                >
                  Consultar
                </Link>
              </div>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
